/**
 * DynamicSelect - A reusable dropdown component that supports adding/removing options
 * Uses the field_options API for persistent storage
 */

import React, { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Plus, Trash2, MoreVertical, Edit2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { fieldOptionsAPI } from '../lib/api';
import { cn } from '../lib/utils';

export function DynamicSelect({
  fieldType,
  value,
  onValueChange,
  placeholder = "Select...",
  parentValue = null,
  disabled = false,
  allowAdd = true,
  allowRemove = true,
  className,
  triggerClassName,
  // For static options (no API persistence)
  staticOptions = null,
  onOptionsChange = null,
}) {
  const [options, setOptions] = useState(staticOptions || []);
  const [loading, setLoading] = useState(!staticOptions);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [newOption, setNewOption] = useState({ value: '', label: '' });
  const [editingOption, setEditingOption] = useState(null);

  // Load options from API
  useEffect(() => {
    if (staticOptions) {
      setOptions(staticOptions);
      return;
    }
    
    loadOptions();
  }, [fieldType, parentValue, staticOptions]);

  const loadOptions = async () => {
    if (staticOptions) return;
    
    try {
      setLoading(true);
      const params = {};
      if (parentValue) params.parent_value = parentValue;
      
      const response = await fieldOptionsAPI.getByType(fieldType, params);
      setOptions(response.data);
    } catch (error) {
      console.error('Failed to load options:', error);
      // Fall back to empty array
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = async () => {
    if (!newOption.value || !newOption.label) {
      toast.error('Please enter both value and label');
      return;
    }

    // Check for duplicates
    if (options.some(o => o.value === newOption.value)) {
      toast.error('An option with this value already exists');
      return;
    }

    try {
      if (staticOptions) {
        // Handle static options (no API)
        const updated = [...options, { ...newOption, sort_order: options.length }];
        setOptions(updated);
        if (onOptionsChange) onOptionsChange(updated);
      } else {
        // Persist to API
        await fieldOptionsAPI.create({
          field_type: fieldType,
          value: newOption.value,
          label: newOption.label,
          parent_value: parentValue,
          sort_order: options.length,
        });
        await loadOptions();
      }
      
      toast.success('Option added');
      setNewOption({ value: '', label: '' });
      setShowAddDialog(false);
    } catch (error) {
      toast.error('Failed to add option');
    }
  };

  const handleRemoveOption = async (optionToRemove) => {
    // Don't allow removing system options
    if (optionToRemove.is_system) {
      toast.error('Cannot remove system options');
      return;
    }

    try {
      if (staticOptions) {
        const updated = options.filter(o => o.value !== optionToRemove.value);
        setOptions(updated);
        if (onOptionsChange) onOptionsChange(updated);
      } else {
        await fieldOptionsAPI.delete(optionToRemove.id);
        await loadOptions();
      }
      
      // Clear selection if removed option was selected
      if (value === optionToRemove.value) {
        onValueChange('');
      }
      
      toast.success('Option removed');
    } catch (error) {
      toast.error('Failed to remove option');
    }
  };

  const handleUpdateOption = async () => {
    if (!editingOption) return;

    try {
      if (staticOptions) {
        const updated = options.map(o => 
          o.value === editingOption.originalValue 
            ? { ...o, label: editingOption.label }
            : o
        );
        setOptions(updated);
        if (onOptionsChange) onOptionsChange(updated);
      } else {
        await fieldOptionsAPI.update(editingOption.id, {
          label: editingOption.label,
        });
        await loadOptions();
      }
      
      toast.success('Option updated');
      setEditingOption(null);
    } catch (error) {
      toast.error('Failed to update option');
    }
  };

  const generateValue = (label) => {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  return (
    <div className={cn("relative", className)}>
      <div className="flex gap-1">
        <Select value={value} onValueChange={onValueChange} disabled={disabled || loading}>
          <SelectTrigger className={triggerClassName} data-testid={`select-${fieldType}`}>
            <SelectValue placeholder={loading ? "Loading..." : placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span className={option.color}>{option.label}</span>
              </SelectItem>
            ))}
            {options.length === 0 && !loading && (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                No options available
              </div>
            )}
          </SelectContent>
        </Select>
        
        {(allowAdd || allowRemove) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 shrink-0"
                data-testid={`manage-${fieldType}-options`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {allowAdd && (
                <DropdownMenuItem onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add New Option
                </DropdownMenuItem>
              )}
              {(allowAdd || allowRemove) && options.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowManageDialog(true)}>
                    <Edit2 className="h-4 w-4 mr-2" />
                    Manage Options
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Add Option Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Option</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label (Display Name)</Label>
              <Input
                placeholder="e.g., My Custom Type"
                value={newOption.label}
                onChange={(e) => setNewOption({
                  ...newOption,
                  label: e.target.value,
                  value: generateValue(e.target.value)
                })}
                data-testid="new-option-label"
              />
            </div>
            <div className="space-y-2">
              <Label>Value (Internal ID)</Label>
              <Input
                placeholder="e.g., my_custom_type"
                value={newOption.value}
                onChange={(e) => setNewOption({
                  ...newOption,
                  value: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                })}
                data-testid="new-option-value"
              />
              <p className="text-xs text-muted-foreground">
                Lowercase letters, numbers, and underscores only
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddOption} data-testid="save-new-option">
              Add Option
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Options Dialog */}
      <Dialog open={showManageDialog} onOpenChange={setShowManageDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Options</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2 py-4">
            {options.map((option) => (
              <div 
                key={option.value} 
                className="flex items-center justify-between p-3 rounded-lg border bg-card"
              >
                <div className="flex-1">
                  {editingOption?.value === option.value ? (
                    <Input
                      value={editingOption.label}
                      onChange={(e) => setEditingOption({
                        ...editingOption,
                        label: e.target.value
                      })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleUpdateOption();
                        if (e.key === 'Escape') setEditingOption(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <div>
                      <span className={cn("font-medium", option.color)}>
                        {option.label}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({option.value})
                      </span>
                      {option.is_system && (
                        <span className="text-xs bg-muted px-1.5 py-0.5 rounded ml-2">
                          System
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 ml-2">
                  {editingOption?.value === option.value ? (
                    <>
                      <Button size="sm" onClick={handleUpdateOption}>Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingOption(null)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => setEditingOption({
                          ...option,
                          originalValue: option.value
                        })}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      {allowRemove && !option.is_system && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleRemoveOption(option)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
            {options.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No options yet. Add your first one!
              </div>
            )}
          </div>
          <DialogFooter>
            {allowAdd && (
              <Button variant="outline" onClick={() => {
                setShowManageDialog(false);
                setShowAddDialog(true);
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add New
              </Button>
            )}
            <Button onClick={() => setShowManageDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Hook to fetch and manage field options
 */
export function useFieldOptions(fieldType, parentValue = null) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (parentValue) params.parent_value = parentValue;
      
      const response = await fieldOptionsAPI.getByType(fieldType, params);
      setOptions(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load options');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, [fieldType, parentValue]);

  return { options, loading, error, reload: loadOptions };
}

export default DynamicSelect;
